/**
 * **背景的"情绪"** —— 把 `energy` 翻译成色调与亮度，外加 Bar 那一下的红闪。
 *
 * 评审：
 *
 * > 背景太亮，我希望它有能根据情绪精细 tweak 色调：
 * > - 总是降低一定亮度
 * > - Bar 触发时，亮度提高，色调泛红，0.5 秒内消退
>   （⚠️ 后续澄清：这一下**和 Bar 那圈涟漪一起，只在濒死期间**触发 ——
>    见 `GameBackdrop.barTick`，那里是一个判断罩住涟漪 + 泛红 + 濒死重音）
 * > - 将 `1 + energy * 0.1` 作为 `Background.#timeScale` 的倍率
 * > - energy 越高色调越橙且亮度越高，反之色调偏蓝且亮度降低
 * > - 以上计算中，energy 取值要先 `clamp(-10, energy, 12)`
 *
 * 这个文件是**纯函数**：输入一个数，输出"该把画面调成什么样"。
 * 于是"什么时候泛红、红多少"能在 node 里断言，而不是靠肉眼对着截图猜。
 *
 * ## 三层叠在一起
 *
 * ```
 *   ① 静态的"暗"      DIM_BASE              永远先压暗（背景太亮是评审的原始诉求）
 *   ② energy 的倾向    + LIFT × t            t 由 energy 归一化来：高→亮且橙，低→暗且蓝
 *   ③ Bar 那一下       + FLASH_* × flash     flash 从 1 指数衰到 0（约 0.5 秒）
 * ```
 *
 * ⚠️ ①永远生效：**连 energy 拉满时也不超过 1** —— "总是降低一定亮度"是硬要求，
 * 不能让"高能量"把它抵消掉。
 */

/** 调色板式的常量。要改观感改这里，别去改公式 */
export const MOOD = {
  /** energy 的夹取范围（评审给的那两个数） */
  ENERGY_MIN: -10,
  ENERGY_MAX: 12,
  /**
   * 时间倍率的系数：`1 + energy * 0.1`。
   *
   * ⚠️ 评审在手调这个数（原来是 0.05）—— 别再照着自己的理解往回改。
   */
  TIME_PER_ENERGY: 0.1,
  /**
   * **倍率的下限。**
   *
   * ⚠️ 没有它的话 `energy = -10`（下限）时 `1 + (-10) × 0.1 = 0` —— 场**冻住**。
   *    评审要的是"低能量时更沉"，不是"停"。所以把**系数**夹在这个值以上。
   *    高能量那一头不受影响（`1 + 12 × 0.1 = 2.2` 照旧）。
   */
  TIME_FACTOR_MIN: 0.15,

  /** ① 永远先压暗这么多 */
  DIM_BASE: 0.05,
  /** ② energy 拉满时相对抬回来多少。`DIM_BASE + LIFT < 1` —— 见文件头 */
  LIFT: 0.1,
  /** 冷端（低 energy）：偏蓝 */
  COOL: [0.8, 0.9, 1.12] as const,
  /** 暖端（高 energy）：偏橙 */
  WARM: [1.16, 0.94, 0.7] as const,

  /**
   * ③ Bar 那一下：亮度**相对**抬多少（乘性）。
   *
   * ⚠️ 原来是一个**绝对**加法（`brightness + 0.45`）。在 `DIM_BASE = 0.7` 那些
   *    数下没事，但 `DIM_BASE` 一调小（评审手调到过 0.05），同一发 Bar
   *    就把背景一次抬 10 倍 —— 画面上看是"背景**突然变大**"（点全亮起来了），
   *    而人眼把它读成尺寸变化。改成乘性之后：
   *
   *      旧：0.7 + 0.45 = 1.15      新：0.7 × 1.65 = 1.155   ← 观感不变
   *      旧：0.05 + 0.45 = 0.50     新：0.05 × 1.65 = 0.08    ← 不再跳十倍
   */
  FLASH_GAIN: 0.65,
  /** Bar 那一下的色调（红） */
  FLASH_TINT: [1.9, 0.5, 0.42] as const,
  /** 消退速率（1/秒）。`exp(-6 × 0.5) ≈ 0.05` —— 半秒内基本没了 */
  FLASH_RATE: 6,
  /** "消退完"的判据（秒）。评审说的是"0.5 秒内消退"，测试拿它当上限 */
  FLASH_FADE_SECONDS: 0.5,
  /** **濒死时场的播放速度翻倍**（评审）。见 `withDyingSpeed()` */
  DYING_SPEED_MULTIPLIER: 2,
} as const;

export type Rgb = readonly [number, number, number];

export interface Mood {
  /** 亮度倍率。**永远 < 1**（除了 Bar 那一下会短暂抬到 1 以上） */
  readonly brightness: number;
  /** 颜色倍率。以 1 为中性 */
  readonly tint: Rgb;
}

/** 中性（不调）：调试页"关掉情绪"时用它 */
export const NEUTRAL_MOOD: Mood = Object.freeze({ brightness: 1, tint: [1, 1, 1] as Rgb });

/**
 * 评审指定的夹取。`NaN` 当 0 处理 —— 它会一路传进 uniform，让整块背景消失，
 * 而那种症状（"背景偶尔不见了"）根本没法查。`±Infinity` 按正常的夹取走
 * （它们是有序的，`min/max` 处理得了）。
 */
export function clampEnergy(energy: number): number {
  if (Number.isNaN(energy)) return 0;
  return Math.min(MOOD.ENERGY_MAX, Math.max(MOOD.ENERGY_MIN, energy));
}

/** energy → 0..1 的归一化位置（-10 → 0、12 → 1） */
export function moodT(energy: number): number {
  const span = MOOD.ENERGY_MAX - MOOD.ENERGY_MIN;
  return span <= 0 ? 0 : (clampEnergy(energy) - MOOD.ENERGY_MIN) / span;
}

/**
 * energy → 静态情绪（不含 Bar 那一闪）。
 *
 * ⚠️ 单调：energy 越高越亮、越橙。两条都有断言盯着 ——
 * 手感错了只是"看着有点怪"，没人会报 bug。
 */
export function moodFor(energy: number): Mood {
  const t = moodT(energy);
  const brightness = MOOD.DIM_BASE + MOOD.LIFT * t;
  return {
    brightness,
    tint: mixRgb(MOOD.COOL, MOOD.WARM, t),
  };
}

/**
 * 把 Bar 那一下叠上去。
 *
 * `flash` 是 0..1（`barFlash()` 硬赋 1，然后每帧衰减）。它同时抬高亮度、
 * 把色调往红里拉 —— 评审要的是"亮一下 + 泛红"。
 */
export function moodWithFlash(mood: Mood, flash: number): Mood {
  const f = Number.isFinite(flash) ? Math.min(1, Math.max(0, flash)) : 0;
  if (f <= 0) return mood;
  return {
    // ⚠️ 乘性（见 `FLASH_GAIN`）：绝对加法会在背景本来就暗的时候抬十倍
    brightness: mood.brightness * (1 + MOOD.FLASH_GAIN * f),
    tint: mixRgb(mood.tint, MOOD.FLASH_TINT, f),
  };
}

/** 一闪衰减一步（墙上时间）。指数下降 = easeout，起点硬、尾巴软 */
export function decayFlash(flash: number, dt: number): number {
  if (!Number.isFinite(flash) || flash <= 0) return 0;
  if (dt <= 0) return flash;
  const next = flash * Math.exp(-dt * MOOD.FLASH_RATE);
  return next < 0.004 ? 0 : next;
}

/**
 * `energy` 对**播放速度**的影响：评审给的 `1 + energy * 0.1`。
 *
 * 高能量时背景动得更快（更躁），低能量时更慢（更沉）。
 * energy 已经 clamp 过，所以倍率被夹在 `[0.5, 1.6]`。
 */
export function timeScaleFor(base: number, energy: number): number {
  const factor = 1 + clampEnergy(energy) * MOOD.TIME_PER_ENERGY;
  return base * Math.max(MOOD.TIME_FACTOR_MIN, factor);
}

/**
 * **濒死时场的播放速度翻倍。**
 *
 * 评审：「计算完播放速度并应用上下限后，若当前是濒死，则将播放速度翻倍」。
 *
 * ⚠️ 顺序是**先算、先夹、再翻倍**：`timeScaleFor()` 里那道 `TIME_FACTOR_MIN`
 *    下限是给"低能量时更沉"用的，翻倍发生在它之后 —— 所以濒死时**允许超过**
 *    平时那个上限（那是故意的：那一刻场就是要躁起来）。
 */
export function withDyingSpeed(base: number, energy: number, dying: boolean): number {
  const factor = timeScaleFor(base, energy);
  return dying ? factor * MOOD.DYING_SPEED_MULTIPLIER : factor;
}

/** 两个颜色按 t 混。`t` 已经由调用方保证在 0..1 */
function mixRgb(a: Rgb, b: Rgb, t: number): Rgb {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ];
}
